import type { IAddress, IPharmacyAddress } from '../common.dto';

/**
 * Delivery details flattened onto — and read back off — a gateway intent.
 *
 * When a payment completes somewhere we do not control (Stripe's 3DS redirect,
 * Whop's embedded checkout), the intent metadata is the *only* copy of the
 * address that survives a browser which never comes back. Recovery flows
 * rebuild the order from it.
 *
 * The writer and the reader live in one file on purpose: they are a single
 * wire format, and a drift between them would silently produce orders with
 * empty addresses rather than an error.
 */

export interface IntentAddresses {
  shippingAddress?: IAddress;
  pharmacyAddress?: IPharmacyAddress;
  transferOut?: boolean;
}

export function buildIntentAddressMetadata({
  shippingAddress,
  pharmacyAddress,
  transferOut
}: IntentAddresses): Record<string, string> {
  return transferOut
    ? {
        pharmacyAddressName: pharmacyAddress?.name || '',
        pharmacyAddress1: pharmacyAddress?.address1 || '',
        pharmacyAddress2: pharmacyAddress?.address2 || '',
        pharmacyCity: pharmacyAddress?.city || '',
        pharmacyState: pharmacyAddress?.provinceCode || '',
        pharmacyPostalCode: pharmacyAddress?.postalCode || '',
        pharmacyCountry: pharmacyAddress?.countryCode || '',
        pharmacyPhoneNumber: pharmacyAddress?.phoneNumber || '',
        pharmacyFaxNumber: pharmacyAddress?.faxNumber || '',
        transferOut: transferOut ? 'true' : 'false'
      }
    : {
        shippingAddress1: shippingAddress?.address1 || '',
        shippingAddress2: shippingAddress?.address2 || '',
        shippingCity: shippingAddress?.city || '',
        shippingState: shippingAddress?.provinceCode || '',
        shippingPostalCode: shippingAddress?.postalCode || '',
        shippingCountry: shippingAddress?.countryCode || ''
      };
}

export function addressesFromIntentMetadata(
  metadata: Record<string, unknown> = {}
): IntentAddresses {
  const value = (key: string) => (metadata?.[key] as string) || '';

  if (value('transferOut') === 'true') {
    const hasPharmacy =
      value('pharmacyAddressName') || value('pharmacyAddress1');
    return {
      transferOut: true,
      pharmacyAddress: hasPharmacy
        ? ({
            name: value('pharmacyAddressName'),
            address1: value('pharmacyAddress1'),
            address2: value('pharmacyAddress2'),
            city: value('pharmacyCity'),
            provinceCode: value('pharmacyState'),
            postalCode: value('pharmacyPostalCode'),
            countryCode: (value('pharmacyCountry') as 'CA' | 'US') || 'CA',
            phoneNumber: value('pharmacyPhoneNumber'),
            faxNumber: value('pharmacyFaxNumber')
          } as IPharmacyAddress)
        : undefined
    };
  }

  return {
    shippingAddress: {
      address1: value('shippingAddress1'),
      address2: value('shippingAddress2'),
      city: value('shippingCity'),
      provinceCode: value('shippingState'),
      postalCode: value('shippingPostalCode'),
      countryCode: (value('shippingCountry') as 'CA' | 'US') || 'CA'
    }
  };
}
